/* eslint-disable max-lines */
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnFiltersState } from '@tanstack/react-table';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { GridAction } from '@components/atoms/datatable/grid-action-controller';
import Loader from '@components/atoms/loaders/loader';
import Modal from '@components/atoms/modals/modal';
import Button from '@components/atoms/ui-kit/button';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { Elements } from '@utils/providers/theme-context';
import { useGetOnPremOrganizations } from '@utils/api/back-office/on-prem-billing/use-get-on-prem-organizations';
import { useGetOnPremUsageReport } from '@utils/api/back-office/on-prem-billing/use-get-on-prem-usage-report';
import { OnPremOrganization } from '@utils/types/back-office/on-prem-billing';
import {
  PencilLine,
  Server,
  Calendar,
  Mic,
  AudioWaveform,
  Languages,
  GraduationCap,
  HardDrive,
  FileDown
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import cn from 'classnames';
import { formatCurrency } from '@utils/helpers/currency';
import { SupportedCurrency } from '@utils/types/organization/organization';

type Row = OnPremOrganization;

/* eslint-disable no-restricted-syntax -- Semantic on-prem badge color */
const ON_PREM_BADGE_STYLE = {
  backgroundColor: 'rgba(99, 102, 241, 0.15)',
  color: '#4F46E5'
};
/* eslint-enable no-restricted-syntax */

/* eslint-disable no-restricted-syntax -- Per-metric semantic tints for usage cards */
const USAGE_TINTS = {
  indigo: { bg: 'rgba(99, 102, 241, 0.12)', fg: '#6366F1' },
  cyan: { bg: 'rgba(6, 182, 212, 0.12)', fg: '#06B6D4' },
  emerald: { bg: 'rgba(16, 185, 129, 0.12)', fg: '#10B981' },
  amber: { bg: 'rgba(245, 158, 11, 0.14)', fg: '#D97706' },
  rose: { bg: 'rgba(244, 63, 94, 0.12)', fg: '#F43F5E' },
  violet: { bg: 'rgba(139, 92, 246, 0.12)', fg: '#8B5CF6' }
} as const;
/* eslint-enable no-restricted-syntax */

type UsageTint = (typeof USAGE_TINTS)[keyof typeof USAGE_TINTS];

interface OnPremBillingTabProps {
  currency?: SupportedCurrency;
}

export default function OnPremBillingTab({
  currency = 'EUR'
}: OnPremBillingTabProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const pageSize = 25;

  // Table server-side state
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState<string | undefined>('organization_name');
  const [direction, setDirection] = useState<'asc' | 'desc'>('asc');
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [organizationId, setOrganizationId] = useState<number | undefined>(
    undefined
  );
  const [planSku, setPlanSku] = useState<string | undefined>(undefined);

  // Report modal state
  const [selectedOrg, setSelectedOrg] = useState<OnPremOrganization | null>(
    null
  );
  const [reportModalOpen, setReportModalOpen] = useState(false);

  // Date range for report
  const getDefaultDateRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: firstDay.toISOString().split('T')[0],
      end: lastDay.toISOString().split('T')[0]
    };
  };

  const [dateRange, setDateRange] = useState(getDefaultDateRange);

  const { data: orgsData, isLoading: orgsLoading } = useGetOnPremOrganizations({
    offset,
    limit: pageSize,
    sort,
    direction,
    search,
    email,
    organizationId,
    planSku
  });

  const { data: reportData, isLoading: reportLoading } =
    useGetOnPremUsageReport(
      selectedOrg?.organization_id,
      reportModalOpen ? dateRange.start : undefined,
      reportModalOpen ? dateRange.end : undefined
    );

  const BE_SORT_FIELDS: Record<string, string> = {
    organization_id: 'organization_id',
    organization_name: 'organization_name',
    email: 'email',
    plan_sku: 'plan_sku'
  };

  const handlePaginationChange = useCallback(
    (pagination: { pageIndex: number; pageSize: number }) => {
      setOffset(pagination.pageIndex * pagination.pageSize);
    },
    []
  );

  const handleSortChange = useCallback(
    (sortingState: Array<{ id: string; desc: boolean }>) => {
      if (sortingState.length === 0) {
        setSort(undefined);
        setDirection('asc');
        return;
      }
      const mapped = BE_SORT_FIELDS[sortingState[0].id];
      setSort(mapped ?? undefined);
      setDirection(sortingState[0].desc ? 'desc' : 'asc');
    },
    []
  );

  const handleFilterChange = useCallback((filters: ColumnFiltersState) => {
    const find = (id: string) => filters.find(f => f.id === id)?.value;
    setOrganizationId(
      find('organization_id') != null
        ? Number(find('organization_id'))
        : undefined
    );
    setSearch(
      find('organization_name') ? String(find('organization_name')) : undefined
    );
    setEmail(find('email') ? String(find('email')) : undefined);
    setPlanSku(find('plan_sku') ? String(find('plan_sku')) : undefined);
    setOffset(0);
  }, []);

  const rows = useMemo<Row[]>(() => orgsData?.result ?? [], [orgsData]);

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        id: 'organization_id',
        header: t('id'),
        field: 'organization_id',
        accessorKey: 'organization_id',
        maxWidth: 80,
        minWidth: 70,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'number',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'organization_name',
        header: t('name'),
        field: 'organization_name',
        accessorKey: 'organization_name',
        minWidth: 200,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => (
          <span className="font-medium">{String(getValue() ?? '')}</span>
        )
      },
      {
        id: 'email',
        header: t('email'),
        field: 'email',
        accessorKey: 'email',
        minWidth: 200,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '—')
      },
      {
        id: 'plan_sku',
        header: t('subscription'),
        field: 'plan_sku',
        accessorKey: 'plan_sku',
        minWidth: 150,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => {
          const sku = getValue() as string | undefined;
          if (!sku) {
            return <span style={{ color: getColour('inactiveText') }}>—</span>;
          }
          return (
            <span
              className="px-2 py-0.5 rounded-2xl text-xs font-medium"
              style={{ backgroundColor: getColour('thirdLayer') }}
            >
              {sku}
            </span>
          );
        }
      },
      {
        id: 'billing_channel',
        header: t('billing-channel'),
        field: 'organization_id',
        accessorKey: 'organization_id',
        minWidth: 120,
        maxWidth: 140,
        enableSorting: false,
        enableFiltering: false,
        cell: () => (
          <div
            className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
            style={ON_PREM_BADGE_STYLE}
          >
            <Server className="h-3 w-3" />
            <span>On-Prem</span>
          </div>
        )
      }
    ],
    [t, getColour]
  );

  const handleViewReport = useCallback((org: OnPremOrganization) => {
    setSelectedOrg(org);
    setDateRange(getDefaultDateRange());
    setReportModalOpen(true);
  }, []);

  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'edit',
        label: t('view-usage-report'),
        icon: <PencilLine className="h-3.5 w-3.5" />,
        intent: 'primary',
        run: handleViewReport
      }
    ],
    [t, handleViewReport]
  );

  const fmt = (value: number) => formatCurrency(value, currency);

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  return (
    <div className="py-4 flex-1 min-h-0 flex flex-col">
      {orgsLoading ? (
        <div className="h-64 w-full flex justify-center items-center">
          <Loader height={10} width={3} />
        </div>
      ) : (
        <DataGridV2<Row>
          data={rows}
          columns={columns}
          actions={actions}
          enableRowSelection={false}
          enablePagination
          manualPagination
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          showColumnFilters={false}
          filterMode="popover"
          totalItems={orgsData?.total_results ?? 0}
          pageSize={pageSize}
          pageSizeOptions={[25, 50, 75, 100]}
          fill
          dense
          grid={false}
          striped
          loading={orgsLoading}
          getRowId={row => String(row.organization_id)}
          onRowClick={handleViewReport}
          onPaginationChange={handlePaginationChange}
          onSortingChange={handleSortChange}
          onFilteringChange={handleFilterChange}
          defaultSort={[{ id: 'organization_name', desc: false }]}
        />
      )}

      {/* Usage Report Modal */}
      <Modal
        isOpen={reportModalOpen}
        onClose={() => {
          setReportModalOpen(false);
          setSelectedOrg(null);
        }}
        size="large"
        title={
          selectedOrg
            ? `${t('usage-report')}: ${selectedOrg.organization_name}`
            : t('usage-report')
        }
        footerActions={[
          <Button
            key="close"
            label={t('close')}
            variant="smoke"
            size="small"
            onClick={() => {
              setReportModalOpen(false);
              setSelectedOrg(null);
            }}
          />
        ]}
      >
        <div className="space-y-6">
          {/* Date Range Selector */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar
                className="h-4 w-4"
                style={{ color: getColour('inactiveText') }}
              />
              <label
                className="text-sm"
                style={{ color: getColour('normalText') }}
              >
                {t('date-range')}:
              </label>
            </div>
            <input
              type="date"
              value={dateRange.start}
              onChange={e =>
                setDateRange(prev => ({ ...prev, start: e.target.value }))
              }
              className="px-3 py-1.5 rounded-full text-sm border border-hairline bg-app-soft"
              style={{ color: getColour('normalText') }}
            />
            <span style={{ color: getColour('inactiveText') }}>→</span>
            <input
              type="date"
              value={dateRange.end}
              onChange={e =>
                setDateRange(prev => ({ ...prev, end: e.target.value }))
              }
              className="px-3 py-1.5 rounded-full text-sm border border-hairline bg-app-soft"
              style={{ color: getColour('normalText') }}
            />
          </div>

          {reportLoading ? (
            <div className="flex justify-center py-8">
              <Loader height={6} width={2} />
            </div>
          ) : reportData?.result ? (
            <>
              {/* Usage Section */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="block w-1 h-4 rounded-full"
                    style={{ backgroundColor: getColour('borderPrimary') }}
                  />
                  <h3
                    className="text-sm font-semibold"
                    style={{ color: getColour('normalText') }}
                  >
                    {t('usage')}
                  </h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <UsageCard
                    label={t('stt-units')}
                    value={formatNumber(reportData.result.stt_units_used)}
                    icon={Mic}
                    tint={USAGE_TINTS.indigo}
                    getColour={getColour}
                  />
                  <UsageCard
                    label={t('alignment-units')}
                    value={formatNumber(reportData.result.alignment_units_used)}
                    icon={AudioWaveform}
                    tint={USAGE_TINTS.cyan}
                    getColour={getColour}
                  />
                  <UsageCard
                    label={t('translation-chars')}
                    value={formatNumber(
                      reportData.result.translation_characters_used
                    )}
                    icon={Languages}
                    tint={USAGE_TINTS.emerald}
                    getColour={getColour}
                  />
                  <UsageCard
                    label={t('models-trained')}
                    value={formatNumber(
                      reportData.result.custom_models_trained
                    )}
                    icon={GraduationCap}
                    tint={USAGE_TINTS.amber}
                    getColour={getColour}
                  />
                  <UsageCard
                    label={t('models-hosted')}
                    value={formatNumber(reportData.result.custom_models_hosted)}
                    icon={HardDrive}
                    tint={USAGE_TINTS.rose}
                    getColour={getColour}
                  />
                  <UsageCard
                    label={t('templates-exported')}
                    value={formatNumber(
                      reportData.result.stt_templates_exported
                    )}
                    icon={FileDown}
                    tint={USAGE_TINTS.violet}
                    getColour={getColour}
                  />
                </div>
              </div>

              {/* Total Cost */}
              <div>
                <h3
                  className="text-sm font-semibold mb-3"
                  style={{ color: getColour('normalText') }}
                >
                  {t('estimated-costs')}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <CostCard
                    label={t('total-estimated-cost')}
                    value={fmt(parseFloat(reportData.result.total_cost))}
                    getColour={getColour}
                    highlight
                  />
                </div>
              </div>
            </>
          ) : (
            <div
              className="text-center py-8"
              style={{ color: getColour('inactiveText') }}
            >
              {t('no-usage-data')}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

// Helper components
function UsageCard({
  label,
  value,
  icon: Icon,
  tint,
  getColour
}: {
  label: string;
  value: string;
  icon: LucideIcon;
  tint: UsageTint;
  getColour: (element: Elements) => string;
}) {
  return (
    <div className="group relative p-3 rounded-2xl border border-hairline bg-app-soft shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:shadow-card-hover overflow-hidden">
      <div
        className="absolute inset-x-0 top-0 h-0.5 opacity-70"
        style={{ backgroundColor: tint.fg }}
      />
      <div className="flex items-start gap-2.5">
        <div
          className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-transform duration-200 group-hover:scale-110"
          style={{ backgroundColor: tint.bg, color: tint.fg }}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div
            className="text-[10px] font-medium uppercase tracking-wider truncate"
            style={{ color: getColour('inactiveText') }}
            title={label}
          >
            {label}
          </div>
          <div
            className="text-lg font-semibold leading-tight mt-0.5 truncate"
            style={{ color: getColour('normalText') }}
            title={value}
          >
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

function CostCard({
  label,
  value,
  getColour,
  highlight = false
}: {
  label: string;
  value: string;
  getColour: (element: Elements) => string;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        'p-3 rounded-2xl border border-hairline bg-app-soft shadow-card',
        highlight && 'ring-2 ring-green-500'
      )}
      style={
        highlight ? { backgroundColor: 'rgba(16, 185, 129, 0.1)' } : undefined
      }
    >
      <div
        className="text-xs mb-1"
        // eslint-disable-next-line no-restricted-syntax -- Semantic highlight color
        style={{ color: highlight ? '#059669' : getColour('inactiveText') }}
      >
        {label}
      </div>
      <div
        className={cn('text-lg font-semibold', highlight && 'text-green-600')}
        // eslint-disable-next-line no-restricted-syntax -- Semantic highlight color
        style={{ color: highlight ? '#059669' : getColour('normalText') }}
      >
        {value}
      </div>
    </div>
  );
}
